import { getIdQueryString, getReferenceId } from '@quromedical/fhir-common';
import type { PartialTaskWithId, TaskSearchStatus } from '@quromedical/models';
import type { PrimitiveValueType } from 'components/types';
import { mapDisplaysToOptionsWithStart } from 'core/forms';
import { SkeletonProvider } from 'design-system';
import { ActionList } from 'design-system/components';
import { useUserSession } from 'hooks';
import { useFiniteState } from 'hooks/useFiniteState';
import type React from 'react';
import { type PropsWithChildren, useCallback } from 'react';
import { strings } from 'strings';

import {
  isSupportedFilter,
  isSupportedOwnerFilter,
  type OwnerFilter,
  ownerFilterDisplays,
  type StatusFilter,
  type SupportedFilterStatus,
  statusFilterDisplays,
} from './internal';

interface ListProps extends PropsWithChildren {
  onFilterChange?: (status?: TaskSearchStatus) => void;
  onFilterSecondaryChange?: (status?: OwnerFilter) => void;
  loading?: boolean;
  tasks?: PartialTaskWithId[];
}

const statusFilters = mapDisplaysToOptionsWithStart<
  SupportedFilterStatus,
  'all'
>(
  {
    value: 'all',
    display: 'All',
  },
  statusFilterDisplays,
);
const ownerFilters = mapDisplaysToOptionsWithStart<OwnerFilter, 'all'>(
  {
    value: 'all',
    display: 'All',
  },
  ownerFilterDisplays,
);

export const List: React.FC<ListProps> = ({
  loading = false,
  onFilterChange,
  onFilterSecondaryChange,
  children,
}) => {
  const filter = useFiniteState<StatusFilter>('active');
  const handleFilterChange = useCallback(
    (value: PrimitiveValueType) => {
      if (isSupportedFilter(value)) {
        filter.set(value);
        onFilterChange?.(value);
      } else {
        filter.set('all');
        onFilterChange?.(undefined);
      }
    },
    [filter, onFilterChange],
  );

  const owner = useFiniteState<OwnerFilter>('all');
  const session = useUserSession();
  const profile = session.getProfile();

  const profileId = profile?.id;
  const organizationReference = profile?.organizations;
  const organizationsIDs = getIdQueryString(
    organizationReference?.map((s) => getReferenceId(s)),
  );

  const handleSecondaryFilterChange = useCallback(
    (value: PrimitiveValueType) => {
      const filterData: Record<string, string | undefined> = {
        me: profileId,
        team: organizationsIDs,
        all: undefined,
      };

      if (isSupportedOwnerFilter(value)) {
        owner.set(value || 'all');
        onFilterSecondaryChange?.(filterData[value]);
      }
    },

    [profileId, organizationsIDs, owner, onFilterSecondaryChange],
  );

  return (
    <SkeletonProvider loading={loading}>
      <ActionList.List
        blockColor="accent-blue"
        icon="playlist-add-check"
        title={strings.TaskListTitle}
        filterOptions={statusFilters}
        filterValue={filter.value}
        filterSecondaryOptions={ownerFilters}
        filterSecondaryValue={owner.value}
        onFilterChange={handleFilterChange}
        onFilterSecondaryChange={handleSecondaryFilterChange}
        showFilter={!!onFilterChange}
        showSecondaryFilter={!!onFilterSecondaryChange}
      >
        <ActionList.SubHeading
          subtitle={strings.DateLabel}
          title={strings.TaskListTaskHeading}
        />
        {children}
      </ActionList.List>
    </SkeletonProvider>
  );
};
